import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { PageLayout } from '@/components/ui/page-layout';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useAuthStore } from '@features/authentication/store/authStore';
import { PERMISSIONS } from '@features/shared/constants/permissions';
import { FAMILIES, FAMILY_ORDER } from '../config/families';
import { useDocumentCaptureStore } from '../store/documentCaptureStore';
import { DEFAULT_REGISTRY_PAGE_SIZE } from '../config/verification';
import { RETIRE_SWEEP_MS } from '../lib/streamReducers';
import { reconnectDocCapture, subscribeDocCapture } from '../lib/docCaptureBus';
import { ScanWatcher, deriveStationId, isFileSystemAccessSupported } from '../lib/scanWatcher';
import { documentCaptureService } from '../services/documentCaptureService';
import { StationStatusPill, ConnectionBanner, ScanQueueLine } from '../components/StationStatus';
import { TallyStrip } from '../components/TallyStrip';
import { LiveStreamPanel } from '../components/LiveStreamPanel';
import { RegistryPanel } from '../components/RegistryPanel';
import { NeedsReviewCard } from '../components/NeedsReviewCard';
import { SessionCard } from '../components/SessionCard';
import { ReviewOverlay } from '../components/review/ReviewOverlay';
import { SecondReaderDialog } from '../components/admin/SecondReaderDialog';
import { SecondReaderOutageBanner } from '../components/SecondReaderOutageBanner';
import { WelcomeStrip } from '../components/WelcomeStrip';
import type { DocumentRegistryQuery, RegistryIdentificationFilter } from '../types/documentCapture.types';

type Panel = 'stream' | 'registry';

/**
 * Document Capture landing page (86d3rrfbf) — ONE page for the whole service. Scan Station,
 * the archive and the review queue are panels here, not separate routes.
 *
 * Store access is by VALUE selectors throughout (React Compiler — CLAUDE.md 86d3d1z40): never a
 * store getter invoked during render, or the compiler memoizes on the stable function reference
 * and serves stale UI.
 */
export function DocumentCapturePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [secondReaderOpen, setSecondReaderOpen] = useState(false);
  const [panel, setPanel] = useState<Panel>(() =>
    searchParams.get('view') === 'registry' ? 'registry' : 'stream'
  );

  // ---- permissions: which families this user may see, and can they upload at all ----
  //
  // ⚠️ Each of these subscribes to a VALUE, never to the `hasPermission` GETTER. Selecting the getter
  // and calling it in render hides the permission state from the React Compiler, which memoizes on
  // the stable function reference and serves a stale affordance (CLAUDE.md 86d3d1z40 — the same
  // class of bug as the Save button that stayed disabled after a real change). `visiblePrefixes`
  // goes through a joined string because a selector returning a fresh array every call would
  // re-render on every store touch; the string is a stable primitive the compiler can compare.
  const visibleKey = useAuthStore((s) =>
    FAMILY_ORDER.filter((prefix) => s.hasPermission(FAMILIES[prefix].permission)).join(',')
  );
  const visiblePrefixes = useMemo(() => (visibleKey ? visibleKey.split(',') : []), [visibleKey]);
  const canUpload = useAuthStore((s) => s.hasPermission(PERMISSIONS.DOCSCAN_UPLOAD));
  const canManageAi = useAuthStore((s) => s.hasPermission(PERMISSIONS.DOCSCAN_AI_MANAGE));
  // 86d3z2nz4 — a live-stream "Needs review" row now opens the review queue, so the feed needs the
  // same right the rail's rows imply. Without it the row stays inert: a control that 403s is worse
  // than no control. Same VALUE-selector discipline as its neighbours.
  const canReview = useAuthStore((s) => s.hasPermission(PERMISSIONS.DOCSCAN_REVIEW));

  // ---- store (value selectors only) ----
  const stream = useDocumentCaptureStore((s) => s.stream);
  const streamConnection = useDocumentCaptureStore((s) => s.streamConnection);
  const stationState = useDocumentCaptureStore((s) => s.stationState);
  const stationFolderName = useDocumentCaptureStore((s) => s.stationFolderName);
  const intakeToday = useDocumentCaptureStore((s) => s.intakeToday);
  const intakeTodayLoading = useDocumentCaptureStore((s) => s.intakeTodayLoading);
  const intakeTodayError = useDocumentCaptureStore((s) => s.intakeTodayError);
  const documents = useDocumentCaptureStore((s) => s.documents);
  const registryTotal = useDocumentCaptureStore((s) => s.registryTotal);
  const registryLoading = useDocumentCaptureStore((s) => s.registryLoading);
  const registryError = useDocumentCaptureStore((s) => s.registryError);
  const registryQuery = useDocumentCaptureStore((s) => s.registryQuery);
  const reviewCounts = useDocumentCaptureStore((s) => s.reviewCounts);
  const reviewItems = useDocumentCaptureStore((s) => s.reviewItems);
  const reviewActiveTab = useDocumentCaptureStore((s) => s.reviewActiveTab);
  const reviewLoading = useDocumentCaptureStore((s) => s.reviewLoading);
  const reviewError = useDocumentCaptureStore((s) => s.reviewError);

  const applyStreamEvent = useDocumentCaptureStore((s) => s.applyStreamEvent);
  const hydrateStream = useDocumentCaptureStore((s) => s.hydrateStream);
  const sweepStream = useDocumentCaptureStore((s) => s.sweepStream);
  const setStreamConnection = useDocumentCaptureStore((s) => s.setStreamConnection);
  const addLocalJourneyRow = useDocumentCaptureStore((s) => s.addLocalJourneyRow);
  const resolveLocalJourneyRow = useDocumentCaptureStore((s) => s.resolveLocalJourneyRow);
  const setStationState = useDocumentCaptureStore((s) => s.setStationState);
  const setStationQueue = useDocumentCaptureStore((s) => s.setStationQueue);
  const setRegistryQuery = useDocumentCaptureStore((s) => s.setRegistryQuery);
  const fetchDocuments = useDocumentCaptureStore((s) => s.fetchDocuments);
  const setRegistryVisible = useDocumentCaptureStore((s) => s.setRegistryVisible);
  const flushRegistryRefetch = useDocumentCaptureStore((s) => s.flushRegistryRefetch);
  const fetchReviewQueue = useDocumentCaptureStore((s) => s.fetchReviewQueue);
  const fetchIntakeToday = useDocumentCaptureStore((s) => s.fetchIntakeToday);
  const refreshIntakeIfDayChanged = useDocumentCaptureStore((s) => s.refreshIntakeIfDayChanged);
  const setReviewTab = useDocumentCaptureStore((s) => s.setReviewTab);
  const openReview = useDocumentCaptureStore((s) => s.openReview);

  // ---- live stream: hydrate the replay, then subscribe ----
  // Through the shared bus (86d3w2jrj FE-3) rather than opening the EventSource directly: the
  // viewer and the review panels subscribe to the same one, so navigating between them hands the
  // connection over instead of closing and reopening it.
  //
  // ⚠️ The un-awaited `hydrateStream()` and the synchronous `subscribeDocCapture` are BOTH deliberate,
  // and this is the seam where a replayed snapshot used to be applied on top of newer live state.
  // Awaiting the hydrate before subscribing looks like the fix and is the wrong one: it trades a
  // reordering for a GAP, and an event missed in that gap never comes back. Instead the store queues
  // live events for the length of the replay's round trip and drains them AFTER it — see the
  // "hydrate / live ordering" block in documentCaptureStore. The order of these two lines is
  // load-bearing: the first closes the gate synchronously, before the second can deliver anything.
  useEffect(() => {
    void hydrateStream();
    return subscribeDocCapture(applyStreamEvent, setStreamConnection);
  }, [hydrateStream, applyStreamEvent, setStreamConnection]);

  // ---- 86d3yeh5j — finished rows step aside so the feed stays about what needs a person ----
  // The timer lives HERE, in a page effect with cleanup, and not at store module scope: the store
  // loads app-wide (the Sidebar's review badge imports it), so a module-level interval would tick on
  // every route for the whole session. Page-scoped, it also keeps sweeping while the user is on the
  // "All documents" tab — rows finishing behind that tab must age normally, not freeze.
  //
  // Ticking every 1.5s is affordable only because `sweepStream` bails without `set` when nothing
  // changed; without that this page (which subscribes to the whole stream) would re-render forever.
  //
  // 86d3yek7d rides along on the same tick rather than starting a second timer: it compares the day
  // the server stamped on today's numbers with the current Manila date and returns. Without it a
  // station tab left running overnight keeps showing yesterday under a heading that says "Today".
  useEffect(() => {
    const id = setInterval(() => {
      sweepStream();
      refreshIntakeIfDayChanged();
    }, RETIRE_SWEEP_MS);
    return () => clearInterval(id);
  }, [sweepStream, refreshIntakeIfDayChanged]);

  // ---- 86d3yek7d — today's numbers, counted in the database ----
  // The station id is read from the same localStorage key the watcher uploads under, so the header
  // pill asks about the station this browser actually scans as. Sent even without upload rights: it
  // costs nothing, and the pill only renders the count while watching anyway.
  const stationId = useMemo(() => deriveStationId(), []);
  useEffect(() => {
    void fetchIntakeToday(stationId);
  }, [fetchIntakeToday, stationId]);

  // ---- review queue: one fetch feeds tabs + counts + badge ----
  useEffect(() => {
    void fetchReviewQueue();
  }, [fetchReviewQueue]);

  // ---- registry: URL-synced, fetched when the query changes ----
  useEffect(() => {
    // Seed the query from the URL once, so a shared link lands on the same filtered view.
    const fromUrl: DocumentRegistryQuery = {
      page: 1,
      pageSize: DEFAULT_REGISTRY_PAGE_SIZE,
      q: searchParams.get('q') ?? undefined,
      types: searchParams.getAll('type').length ? searchParams.getAll('type') : undefined,
      scannedFrom: searchParams.get('scanned') ?? undefined,
      scannedTo: searchParams.get('scanned') ?? undefined,
      tranFrom: searchParams.get('tran') ?? undefined,
      tranTo: searchParams.get('tran') ?? undefined,
      linkStatus: searchParams.get('link') ? Number(searchParams.get('link')) : undefined,
      // 86d3x48q7 T13 — read AND written below. Both halves are required: a filter that only
      // writes its param survives a share but not a reload, and one that only reads it silently
      // drops off the URL the moment any other filter changes.
      verification: (searchParams.get('ident') as RegistryIdentificationFilter | null) ?? undefined,
    };
    setRegistryQuery(fromUrl);
    void fetchDocuments();
    // Intentionally once on mount: later changes flow through handleQueryChange.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- 86d3z56ma A3 — tell the store which panel is on screen ----
  // The panel stays LOCAL here (it is seeded from `?view=` once and the URL is write-only after
  // that); only a boolean mirror goes to the store, and it clears on unmount so a store that lives
  // app-wide never claims a registry is showing on some other route. This publishes the flag; it
  // deliberately triggers no fetch — entering the tab already fetches, in `switchPanel` below.
  useEffect(() => {
    setRegistryVisible(panel === 'registry');
    return () => setRegistryVisible(false);
  }, [panel, setRegistryVisible]);

  // ---- 86d3z56ma A3 — pay the refetch a hidden tab deferred ----
  // The stream never stops delivering while the browser tab is hidden, so a background refetch is
  // suppressed rather than the events being missed. This is where the one it owes gets paid, so
  // returning to a parked DOP tab never shows a stale archive. Does nothing when nothing is owed.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') flushRegistryRefetch();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [flushRegistryRefetch]);

  const handleQueryChange = useCallback(
    (next: DocumentRegistryQuery) => {
      setRegistryQuery(next);
      void fetchDocuments();

      const params = new URLSearchParams();
      if (panel === 'registry') params.set('view', 'registry');
      if (next.q) params.set('q', next.q);
      next.types?.forEach((t) => params.append('type', t));
      if (next.scannedFrom) params.set('scanned', next.scannedFrom);
      if (next.tranFrom) params.set('tran', next.tranFrom);
      if (next.linkStatus !== undefined) params.set('link', String(next.linkStatus));
      if (next.verification) params.set('ident', next.verification);
      setSearchParams(params, { replace: true });
    },
    [setRegistryQuery, fetchDocuments, setSearchParams, panel]
  );

  // ---- scan station watcher ----
  const watcherRef = useRef<ScanWatcher | null>(null);
  useEffect(() => {
    if (!canUpload) {
      // Without upload rights the station would 403 on every heartbeat and drop.
      setStationState('nofolder', null);
      return;
    }
    if (!isFileSystemAccessSupported()) {
      setStationState('unsupported', null);
      return;
    }
    const watcher = new ScanWatcher({
      onStatus: (status, folderName) => setStationState(status, folderName),
      onFileStart: (key, fileName) => addLocalJourneyRow(key, fileName),
      onFileDone: (key, result) => {
        // Upload success ≠ captured — the server returns Queued (decode pending). Resolve to the
        // honest 'reading' phase and let SSE own the terminal captured/linked/review morphs
        // (86d3uf18j M2). 'duplicate' is a real terminal answer with no server event, so it stays.
        resolveLocalJourneyRow(key, result.duplicate ? 'duplicate' : 'reading', {
          docNumber: result.docNumber,
          prefix: result.docNumber ? result.docNumber.split('-')[0] : null,
          fileId: result.fileId,
          duplicateStatus: result.duplicate
            ? { docStatus: result.docStatus, linkStatus: result.linkStatus }
            : undefined,
        });
      },
      onFileError: (key) => resolveLocalJourneyRow(key, 'failed'),
      // Queue depth for the header line. The watcher already suppresses unchanged reports, so this
      // is not a per-tick write — it fires when the numbers actually move.
      onProgress: (progress) => setStationQueue(progress),
    });
    watcherRef.current = watcher;
    void watcher.tryResumeSilently();
    return () => {
      // dispose(), not stop(): attaching is async, so this cleanup runs before tryResumeSilently
      // resolves. Only an irreversible dispose stops that in-flight attach from starting a SECOND
      // poll loop over the same folder — which opened a duplicate journey row per scan under
      // StrictMode's double-invoke (86d3uf18j).
      watcher.dispose();
      watcherRef.current = null;
    };
  }, [canUpload, setStationState, setStationQueue, addLocalJourneyRow, resolveLocalJourneyRow]);

  const handleStationAction = useCallback(() => {
    const watcher = watcherRef.current;
    if (!watcher) return;
    if (stationState === 'resume') void watcher.resume();
    else void watcher.chooseFolder();
  }, [stationState]);

  /**
   * Switch the watched folder from the Watching pill. The OS picker IS the instant acknowledgement,
   * so there is nothing to render between the click and its answer.
   *
   * Confirmed only on a real pick: `chooseFolder()` resolves to null when the picker is dismissed,
   * and announcing "Now watching…" after a cancel would describe a switch that never happened. The
   * status callback is the wrong place for this — `onStatus('watching')` also fires on the silent
   * resume at page load, which nobody asked for.
   */
  const handleChangeFolder = useCallback(async () => {
    const watcher = watcherRef.current;
    if (!watcher) return;
    const folderName = await watcher.chooseFolder();
    if (!folderName) return;
    toast.success(`Now watching ${folderName}`, {
      description: 'Files already picked up from the old folder are safe — nothing is re-read or lost.',
    });
  }, []);

  /* M3 (86d44z1xm) — the shell's two Services quick actions land here.
     ⛔ NEITHER can fire its own control after navigating. `showDirectoryPicker` (Scan Station) and
     a file input's `.click()` (Upload) BOTH require transient user activation, which an effect
     that runs after a route change does not have — the browser simply refuses. Owner ruling
     2026-08-26: land on the page and PRIME the control so the user's own click carries the
     activation. One extra click, and it actually works. */
  const [primedControl, setPrimedControl] = useState<'upload' | 'scan' | null>(null);
  const scanControlRef = useRef<HTMLDivElement>(null);
  const uploadControlRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const intent = searchParams.get('action');
    if (intent !== 'upload' && intent !== 'scan') return;
    setPrimedControl(intent);
    // Stripped immediately so a refresh or back-navigation does not re-prime.
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!primedControl) return;
    const target = primedControl === 'scan' ? scanControlRef.current : uploadControlRef.current;
    target?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    // A ring alone does not say what to do with it.
    toast.info(
      primedControl === 'scan' ? 'Choose a folder to start watching' : 'Drop or choose files to upload',
      { description: 'Highlighted below.' }
    );
    // Release the highlight rather than leaving the page permanently decorated.
    const timer = window.setTimeout(() => setPrimedControl(null), 8000);
    return () => window.clearTimeout(timer);
  }, [primedControl]);

  /** Manual drop — same pipeline as the watcher, sequential so ten files don't stampede. */
  const handleManualFiles = useCallback(
    async (files: File[]) => {
      for (const file of files) {
        // The correlation id (86d3uf18j) is both the row key and the id sent to the server, so the
        // live stream reconciles this row even if the upload response is lost.
        const clientUploadId = crypto.randomUUID();
        const key = clientUploadId;
        // Row first, upload second — the drop must show something immediately.
        addLocalJourneyRow(key, file.name, clientUploadId);
        try {
          const result = await documentCaptureService.uploadScan(file, undefined, clientUploadId);
            // See onFileDone above (86d3uf18j M2): 'reading' is the honest post-upload phase; SSE
          // owns captured/linked. 'duplicate' has no server event, so it stays terminal here — and
          // carries the existing doc's state (86d3uf18j L2) so the row isn't a bare "duplicate".
          resolveLocalJourneyRow(key, result.duplicate ? 'duplicate' : 'reading', {
            docNumber: result.docNumber,
            prefix: result.docNumber ? result.docNumber.split('-')[0] : null,
            fileId: result.fileId,
            duplicateStatus: result.duplicate
              ? { docStatus: result.docStatus, linkStatus: result.linkStatus }
              : undefined,
          });
        } catch {
          resolveLocalJourneyRow(key, 'failed');
        }
      }
    },
    [addLocalJourneyRow, resolveLocalJourneyRow]
  );

  const switchPanel = (next: Panel) => {
    setPanel(next);
    const params = new URLSearchParams(searchParams);
    if (next === 'registry') params.set('view', 'registry');
    else params.delete('view');
    setSearchParams(params, { replace: true });

    // Re-read the archive on the way in (86d3uu104). The store already refreshes it on capture/link
    // events, so this is the backstop for the case that motivated the fix: if the stream dropped and
    // reconnected, or an event was simply missed, opening the tab must still show current data —
    // "click a filter to make it appear" is not something a user should ever have to learn.
    // Silent: we already have rows cached, so show them immediately and swap in fresh data when it
    // lands. Blanking a list the user just asked to see is the opposite of responsive (86d3uu104).
    if (next === 'registry') void fetchDocuments(true);
  };

  return (
    <TooltipProvider>
      <PageLayout
        title="Document Capture"
        description="Scanned accounting documents, captured by barcode and linked into NetSuite automatically."
        actions={
          // A column, so the queue line can sit directly under the pill it describes — a quiet
          // second line in the header, not a panel competing with the tally strip.
          <div className="flex flex-col items-end gap-1">
            <div className="flex items-center gap-2">
              {/* 86d3x48q7 T13 — invisible without docscan.ai_manage. This surface changes how every
                  scan gets IDENTIFIED, which is a different authority from re-driving a stuck file. */}
              {canManageAi && (
                <Button variant="outline" size="sm" className="h-[30px]" onClick={() => setSecondReaderOpen(true)}>
                  <Sparkles className="mr-1.5 h-3 w-3" aria-hidden />
                  Second reader
                </Button>
              )}
              {/* Wrapper exists to carry the M3 priming ring + scroll target without reaching
                  into StationStatusPill's own markup. */}
              <div
                ref={scanControlRef}
                className={
                  primedControl === 'scan'
                    ? 'rounded-md ring-2 ring-primary ring-offset-2 ring-offset-background'
                    : undefined
                }
              >
                <StationStatusPill
                  state={stationState}
                  folderName={stationFolderName}
                  scannedHereToday={intakeToday?.stationScanned ?? null}
                  // Watching implies a live watcher, which implies docscan.upload — but pass it only
                  // with the right anyway, so the affordance can never appear without one behind it.
                  onChangeFolder={canUpload ? () => void handleChangeFolder() : undefined}
                />
              </div>
            </div>
            {/* Renders nothing until a real batch is in hand; subscribes to the store itself so a
                changing queue depth never re-renders the stream or the archive below. */}
            {canUpload && <ScanQueueLine />}
          </div>
        }
      >
        <WelcomeStrip />

        {/* 86d3yeh4j — ABOVE the connection banner and the tally, because it explains something the
            rows below cannot: scans that stopped moving are WAITING, not broken and not queued for a
            person. Rendered for everyone (it returns null while the reader is healthy); only the
            retry button is permission-gated, inside. */}
        <SecondReaderOutageBanner
          canManageAi={canManageAi}
          onOpenSettings={() => setSecondReaderOpen(true)}
        />

        {canUpload && (
          <ConnectionBanner state={stationState} folderName={stationFolderName} onAction={handleStationAction} />
        )}

        <TallyStrip
          captured={intakeToday?.captured ?? null}
          linked={intakeToday?.linked ?? null}
          review={reviewCounts.total}
          loading={intakeTodayLoading}
        />

        {/* Right rail is 420px per the prototype, not 320. The rail carries the Needs-review queue,
            and its rows now have to say WHY ("Two readers disagree", "Unknown document series") —
            at 320 those wrap to two lines and the panel turns into a wall of text. */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_420px]">
          {/* ---- left: live stream / registry ---- */}
          <div className="min-w-0 rounded-xl border border-border bg-card">
            <div className="flex items-center gap-1 border-b border-border p-2">
              {(['stream', 'registry'] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => switchPanel(value)}
                  aria-pressed={panel === value}
                  className={cn(
                    'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                    panel === value
                      ? 'bg-accent text-accent-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {value === 'stream' ? 'Live stream' : 'All documents'}
                </button>
              ))}
            </div>

            {panel === 'stream' ? (
              <LiveStreamPanel
                rows={stream}
                connection={streamConnection}
                onReconnect={reconnectDocCapture}
                // 86d3z2nz4 — the open queue, already in memory. The row resolves its own item from
                // this at render time, so the click opens the overlay with nothing awaited in front
                // of it, and a row whose item is gone simply stops being clickable.
                reviewItems={reviewItems}
                canReview={canReview}
                onOpenReview={openReview}
              />
            ) : (
              <RegistryPanel
                documents={documents}
                total={registryTotal}
                loading={registryLoading}
                error={registryError}
                query={registryQuery}
                visiblePrefixes={visiblePrefixes}
                onQueryChange={handleQueryChange}
                onRetry={() => void fetchDocuments()}
              />
            )}
          </div>

          {/* ---- right: review + session ---- */}
          <div className="space-y-4">
            <NeedsReviewCard
              counts={reviewCounts}
              activeTab={reviewActiveTab}
              items={reviewItems}
              loading={reviewLoading}
              error={reviewError}
              onTabChange={setReviewTab}
              onOpenItem={(tab, id) => openReview(tab, id)}
              onRetry={() => void fetchReviewQueue()}
            />
            {canUpload && (
              <div
                ref={uploadControlRef}
                className={
                  primedControl === 'upload'
                    ? 'rounded-md ring-2 ring-primary ring-offset-2 ring-offset-background'
                    : undefined
                }
              >
                <SessionCard
                  today={intakeToday}
                  loading={intakeTodayLoading}
                  error={intakeTodayError}
                  onRetryToday={() => void fetchIntakeToday()}
                  // Already emphasised when no watcher is running; priming from the shell's Upload
                  // quick action is the other reason to draw the eye here.
                  emphasised={stationState !== 'watching' || primedControl === 'upload'}
                  onFiles={(files) => void handleManualFiles(files)}
                />
              </div>
            )}
          </div>
        </div>

        {/* Review overlay — opened from the Needs-review card or a flagged stream row. */}
        <ReviewOverlay />

        {/* Mounted only for the permission — the dialog fetches config + stats on open, and an
            unauthorised mount would fire two 403s the moment the page loads. */}
        {canManageAi && <SecondReaderDialog open={secondReaderOpen} onOpenChange={setSecondReaderOpen} />}
      </PageLayout>
    </TooltipProvider>
  );
}
